import React, { useState, useEffect } from 'react';
import {
    Box,
    TextField,
    Typography,
    CircularProgress,
    Alert,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useAddressConversion } from '../../hooks/useAddressConversion';
import { BuildingInfo } from '../../interfaces/Address';

interface LocationAddressFieldProps {
    latitude?: number;
    longitude?: number;
    onAddressUpdate?: (address: string, buildingInfo?: BuildingInfo) => void;
    disabled?: boolean;
    /** Pre-set address for edit mode – skips reverse-geocoding when provided */
    initialAddress?: string;
}

/**
 * LocationAddressField Component
 * This component demonstrates how to use the address conversion functionality
 * to convert coordinates from a map selection to a formatted address.
 * 
 * It should be used in observation forms before the "kaartnummer" field.
 */
const LocationAddressField: React.FC<LocationAddressFieldProps> = ({
    latitude,
    longitude,
    onAddressUpdate,
    disabled = false,
    initialAddress,
}) => {
    const theme = useTheme();
    const { convertCoordinatesToAddress, formatAddress, isLoading, error } = useAddressConversion();
    const [address, setAddress] = useState<string>('');
    const [buildingInfo, setBuildingInfo] = useState<BuildingInfo | null>(null);

    // Convert coordinates to address when they change
    useEffect(() => {
        // If a pre-set address is provided (edit mode), display it directly
        // without triggering reverse-geocoding
        console.log("initialAddress", initialAddress)
        if (initialAddress !== undefined) {
            setAddress(initialAddress);
            return;
        }
        if (latitude && longitude && !disabled) {
            handleAddressConversion();
        }
    }, [latitude, longitude, disabled, initialAddress]);

    const handleAddressConversion = async () => {
        if (!latitude || !longitude) return;

        const result = await convertCoordinatesToAddress({
            lat: latitude,
            lon: longitude,
        });

        if (result) {
            const formattedAddress = formatAddress(result);
            setAddress(formattedAddress);
            setBuildingInfo(result);

            // Notify parent component of the address update
            onAddressUpdate?.(formattedAddress, result);
        } else {
            setAddress('');
            setBuildingInfo(null);
            onAddressUpdate?.('');
        }
    };

    return (
        <Box>
            <Typography
                variant="subtitle2"
                sx={{
                    mb: 1,
                    fontFamily: theme.typography.fontFamily,
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                }}
            >
                Locatie Adres
            </Typography>

            <TextField
                value={address}
                fullWidth
                variant="outlined"
                placeholder="Selecteer een locatie op de kaart om het adres te tonen"
                disabled={disabled || isLoading}
                sx={{
                    '& .MuiInputBase-input': {
                        fontFamily: theme.typography.fontFamily,
                    },
                }}
                InputProps={{
                    readOnly: true,
                    startAdornment: isLoading ? (
                        <CircularProgress size={20} sx={{ mr: 1 }} />
                    ) : null,
                }}
            />

            {/* Error Display */}
            {error && (
                <Alert severity="error" sx={{ mt: 1 }}>
                    <Typography variant="body2">
                        Fout bij het ophalen van het adres: {error}
                    </Typography>
                </Alert>
            )}
        </Box>
    );
};

export default LocationAddressField;
